import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import {
    ArrowLeft,
    Calendar,
    User,
    Phone,
    MapPin,
    Clock,
    Edit,
    CheckCircle,
    Send,
    MessageSquare,
    Loader2,
    History
} from 'lucide-react'
import { format } from 'date-fns'
import { fr, enUS } from 'date-fns/locale'
import toast from 'react-hot-toast'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import './RendezvousDetailPage.css'

registerLocale('fr', fr)

export default function RendezvousDetailPage() {
    const { id } = useParams()
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { hasPermission, hasAnyPermission, user, isSuperAdmin } = useAuthStore()
    const dateLocale = i18n.language === 'fr' ? fr : enUS

    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)

    const [rdv, setRdv] = useState(null)
    const [loading, setLoading] = useState(true)
    const [showQualiteModal, setShowQualiteModal] = useState(false)
    const [showDispatchModal, setShowDispatchModal] = useState(false)
    const [showConseillerModal, setShowConseillerModal] = useState(false)
    const [conseillers, setConseillers] = useState([])
    const [modalLoading, setModalLoading] = useState(false)

    const [qualifications, setQualifications] = useState({ QUALITE: [], CONSEILLER: [] })

    // Modal form states
    const [qualiteForm, setQualiteForm] = useState({ feedbackId: '', feedbackName: '', comment: '', date: null, isTransfert: false })
    const [dispatchForm, setDispatchForm] = useState({ conseiller: '', comment: '' })
    const [conseillerForm, setConseillerForm] = useState({ feedbackId: '', feedbackName: '', comment: '', date: null, isTransfert: false })

    useEffect(() => {
        fetchRendezvous()
        fetchConseillers()
        fetchQualifications()
    }, [id])

    const fetchRendezvous = async () => {
        try {
            const response = await api.get(`/rendezvous/${id}`)
            setRdv(response.data.data.rendezvous)
        } catch (error) {
            toast.error(t('common.error'))
            navigate('/rendezvous')
        } finally {
            setLoading(false)
        }
    }

    const fetchConseillers = async () => {
        try {
            const response = await api.get('/users/conseillers')
            setConseillers(response.data.data.users)
        } catch (error) {
            console.error('Error fetching conseillers:', error)
        }
    }

    const fetchQualifications = async () => {
        try {
            const [qualiteRes, conseillerRes] = await Promise.all([
                api.get('/qualifications', { params: { type: 'QUALITE', etat: 'ENABLED' } }),
                api.get('/qualifications', { params: { type: 'CONSEILLER', etat: 'ENABLED' } })
            ])
            setQualifications({
                QUALITE: qualiteRes.data.data.qualifications,
                CONSEILLER: conseillerRes.data.data.qualifications
            })
        } catch (error) {
            console.error('Error fetching qualifications:', error)
        }
    }

    const handleQualiteSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)
        try {
            await api.post(`/rendezvous/${id}/qualite`, {
                feedback_qualite_rdv: {
                    id: qualiteForm.feedbackId,
                    nom: qualiteForm.feedbackName
                },
                coment_feedback_qualite_rdv: qualiteForm.comment,
                dateHeure_rdv: qualiteForm.date
            })
            toast.success(t('common.success'))
            setShowQualiteModal(false)
            fetchRendezvous()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        } finally {
            setModalLoading(false)
        }
    }

    const handleDispatchSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)
        try {
            await api.post(`/rendezvous/${id}/dispatch`, {
                conseiller_rdv: dispatchForm.conseiller,
                comment_dispatch_rdv: dispatchForm.comment
            })
            toast.success(t('common.success'))
            setShowDispatchModal(false)
            fetchRendezvous()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        } finally {
            setModalLoading(false)
        }
    }

    const handleConseillerSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)
        try {
            await api.post(`/rendezvous/${id}/conseiller`, {
                feedback_conseiller_rdv: {
                    id: conseillerForm.feedbackId,
                    nom: conseillerForm.feedbackName
                },
                comment_feedback_conseiller_rdv: conseillerForm.comment,
                dateHeure_rdv: conseillerForm.date
            })
            toast.success(t('common.success'))
            setShowConseillerModal(false)
            fetchRendezvous()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        } finally {
            setModalLoading(false)
        }
    }

    const canQualify = () => {
        if (!rdv || rdv.etat_rdv !== 'PLANIFIE') return false
        if (isSuperAdmin()) return true
        return hasAnyPermission(['QUALITE_RDV', 'QUALITE_EQUIPE_RDV', 'QUALITE_ALL_RDV'])
    }

    const canDispatch = () => {
        if (!rdv || rdv.etat_rdv !== 'QUALITE_DONE') return false
        if (isSuperAdmin()) return true
        return hasAnyPermission(['DISPATCH_RDV', 'DISPATCH_EQUIPE_RDV', 'DISPATCH_ALL_RDV'])
    }

    const canFeedbackConseiller = () => {
        if (!rdv || rdv.etat_rdv !== 'DISPATCHED') return false
        if (isSuperAdmin()) return true
        return rdv.conseiller_rdv?._id === user._id
    }

    const getStateBadgeClass = (state) => {
        const classes = {
            PLANIFIE: 'badge-info',
            QUALITE_DONE: 'badge-primary',
            DISPATCHED: 'badge-warning',
            CONSEILLER_DONE: 'badge-success',
            SIGNEE: 'badge-success',
            ANNULE: 'badge-error',
            ERREUR: 'badge-error'
        }
        return classes[state] || 'badge-neutral'
    }

    if (loading) {
        return (
            <div className="rdv-detail-loading">
                <Loader2 size={48} className="spin" />
            </div>
        )
    }

    if (!rdv) return null

    return (
        <div className="rdv-detail-page">
            <div className="page-header">
                <button className="btn btn-ghost" onClick={() => navigate('/rendezvous')}>
                    <ArrowLeft size={18} />
                    {t('common.back')}
                </button>
                <div className="page-header-title">
                    <h1 className="page-title">
                        {t('rendezvous.detail')} #{rdv.numeroSequentiel}
                    </h1>
                    {hasPermission('VIEW_STATUS') && (
                        <span className={`badge ${getStateBadgeClass(rdv.etat_rdv)}`}>
                            {t(`rendezvous.states.${rdv.etat_rdv}`)}
                        </span>
                    )}
                </div>
            </div>

            <div className="rdv-detail-grid">
                {/* Main Info */}
                <div className="card">
                    <div className="card-header">
                        <h3>Informations</h3>
                        <div className="card-actions">
                            {hasPermission('MODIF_RDV') && user?.profil?.code_profil !== 'AGENT' && (!['SIGNEE', 'ERREUR', 'SUPPRIME', 'ANNULE'].includes(rdv.etat_rdv) || user?.godMode) && (
                                <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/rendezvous/${id}/edit`)}>
                                    <Edit size={16} />
                                    {t('common.edit')}
                                </button>
                            )}
                            {canQualify() && (
                                <button className="btn btn-secondary btn-sm" onClick={() => setShowQualiteModal(true)}>
                                    <CheckCircle size={16} />
                                    {t('rendezvous.qualite.title')}
                                </button>
                            )}
                            {canDispatch() && (
                                <button className="btn btn-secondary btn-sm" onClick={() => setShowDispatchModal(true)}>
                                    <Send size={16} />
                                    {t('rendezvous.dispatch.title')}
                                </button>
                            )}
                            {canFeedbackConseiller() && (
                                <button className="btn btn-secondary btn-sm" onClick={() => setShowConseillerModal(true)}>
                                    <MessageSquare size={16} />
                                    {t('rendezvous.conseillerFeedback.title')}
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="info-grid">
                            <div className="info-item">
                                <div className="info-icon">
                                    <Calendar size={20} />
                                </div>
                                <div>
                                    <span className="info-label">{t('rendezvous.date')}</span>
                                    <strong>{format(new Date(rdv.dateHeure_rdv), 'PPPp', { locale: dateLocale })}</strong>
                                </div>
                            </div>

                            <div className="info-item">
                                <div className="info-icon">
                                    <User size={20} />
                                </div>
                                <div>
                                    <span className="info-label">{t('rendezvous.agent')}</span>
                                    <strong>{rdv.agent_rdv?.prenom} {rdv.agent_rdv?.nom}</strong>
                                </div>
                            </div>

                            {rdv.equipe_rdv && (
                                <div className="info-item">
                                    <div className="info-icon">
                                        <User size={20} />
                                    </div>
                                    <div>
                                        <span className="info-label">{t('rendezvous.equipe')}</span>
                                        <strong>{rdv.equipe_rdv.nom_equipe}</strong>
                                    </div>
                                </div>
                            )}

                            {rdv.conseiller_rdv && (
                                <div className="info-item">
                                    <div className="info-icon">
                                        <User size={20} />
                                    </div>
                                    <div>
                                        <span className="info-label">{t('rendezvous.conseiller')}</span>
                                        <strong>{rdv.conseiller_rdv.prenom} {rdv.conseiller_rdv.nom}</strong>
                                    </div>
                                </div>
                            )}

                            <div className="info-item">
                                <div className="info-icon">
                                    <Clock size={20} />
                                </div>
                                <div>
                                    <span className="info-label">{t('rendezvous.dateSaisi')}</span>
                                    <strong>{format(new Date(rdv.date_saisi_rdv), 'PPPp', { locale: dateLocale })}</strong>
                                </div>
                            </div>
                        </div>

                        {rdv.commentaireAgent_rdv && (
                            <div className="info-comment">
                                <span className="info-label">{t('rendezvous.comment')}</span>
                                <p>{rdv.commentaireAgent_rdv}</p>
                            </div>
                        )}

                        {/* Feedback sections */}
                        {hasPermission('VIEW_QUALITY_FEEDBACK') && rdv.feedback_qualite_rdv && (
                            <div className="feedback-section">
                                <h4>{t('rendezvous.qualite.title')}</h4>
                                <span className="badge badge-primary">
                                    {rdv.feedback_qualite_rdv.nom || t(`rendezvous.qualite.${rdv.feedback_qualite_rdv}`)}
                                </span>
                                {rdv.coment_feedback_qualite_rdv && <p>{rdv.coment_feedback_qualite_rdv}</p>}
                            </div>
                        )}

                        {hasPermission('VIEW_ADVISOR_FEEDBACK') && rdv.feedback_conseiller_rdv && (
                            <div className="feedback-section">
                                <h4>{t('rendezvous.conseillerFeedback.title')}</h4>
                                <span className="badge badge-success">
                                    {rdv.feedback_conseiller_rdv.nom || t(`rendezvous.conseillerFeedback.${rdv.feedback_conseiller_rdv}`)}
                                </span>
                                {rdv.comment_feedback_conseiller_rdv && <p>{rdv.comment_feedback_conseiller_rdv}</p>}
                            </div>
                        )}
                    </div>
                </div>

                {/* Client Info */}
                <div className="card">
                    <div className="card-header">
                        <h3>Client</h3>
                    </div>
                    <div className="card-body">
                        <div className="client-header">
                            <div className="client-avatar">
                                {rdv.client_rdv?.prenom_client?.[0]}{rdv.client_rdv?.nom_client?.[0]}
                            </div>
                            <div>
                                <h4>{rdv.client_rdv?.prenom_client} {rdv.client_rdv?.nom_client}</h4>
                            </div>
                        </div>

                        <div className="client-info">
                            <div className="info-item">
                                <Phone size={18} />
                                <span>{rdv.client_rdv?.natel_client}</span>
                            </div>
                            {rdv.client_rdv?.fix_client && (
                                <div className="info-item">
                                    <Phone size={18} />
                                    <span>{rdv.client_rdv.fix_client}</span>
                                </div>
                            )}
                            <div className="info-item">
                                <MapPin size={18} />
                                <span>
                                    {rdv.client_rdv?.adresse_client?.adresse}, {rdv.client_rdv?.adresse_client?.npa} {rdv.client_rdv?.adresse_client?.ville}
                                </span>
                            </div>
                        </div>

                        {rdv.client_rdv?.famille?.length > 0 && (
                            <div className="family-section">
                                <h5>{t('family.title')} ({rdv.client_rdv.famille.length})</h5>
                                <div className="family-list-mini">
                                    {rdv.client_rdv.famille.map((member, index) => (
                                        <div key={index} className="family-item">
                                            <span className={`badge ${member.typeFamille === 'Adulte' ? 'badge-primary' : 'badge-info'}`}>
                                                {member.typeFamille}
                                            </span>
                                            <span>
                                                {member.prenomFamille}
                                                {member.datenaissanceFamille ? ` (${format(new Date(member.datenaissanceFamille), 'dd.MM.yyyy')})` : member.age ? ` (${member.age} ans)` : ''}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* History */}
                {hasPermission('VIEW_HISTORY') && rdv.historique_rdv?.length > 0 && (
                    <div className="card history-card">
                        <div className="card-header">
                            <h3>
                                <History size={18} />
                                {t('rendezvous.history')}
                            </h3>
                        </div>
                        <div className="card-body">
                            <div className="history-timeline">
                                {rdv.historique_rdv.map((entry, index) => (
                                    <div key={index} className="history-item">
                                        <div className="history-dot"></div>
                                        <div className="history-content">
                                            <div className="history-header">
                                                <strong>{entry.utilisateur?.nomprenom || 'Système'}</strong>
                                                <span className="badge badge-neutral">{entry.action}</span>
                                            </div>
                                            <small className="text-muted">
                                                {format(new Date(entry.date), 'PPPp', { locale: dateLocale })}
                                            </small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Qualite Modal */}
            {showQualiteModal && (
                <div className="modal-overlay" onClick={() => setShowQualiteModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('rendezvous.qualite.title')}</h3>
                        </div>
                        <form onSubmit={handleQualiteSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">{t('rendezvous.qualite.feedback')} *</label>
                                    <select
                                        className="form-select"
                                        value={qualiteForm.feedbackId}
                                        onChange={(e) => {
                                            const selected = qualifications.QUALITE.find(q => q._id === e.target.value)
                                            if (selected) {
                                                setQualiteForm({
                                                    ...qualiteForm,
                                                    feedbackId: selected._id,
                                                    feedbackName: selected.nomQualification,
                                                    isTransfert: selected.isTransfert
                                                })
                                            }
                                        }}
                                        required
                                    >
                                        <option value="">-</option>
                                        {qualifications.QUALITE.map(q => (
                                            <option key={q._id} value={q._id}>{q.nomQualification}</option>
                                        ))}
                                    </select>
                                </div>
                                {qualiteForm.isTransfert && (
                                    <div className="form-group">
                                        <label className="form-label">{t('rendezvous.qualite.newDate')}</label>
                                        <DatePicker
                                            selected={qualiteForm.date}
                                            onChange={(date) => setQualiteForm({ ...qualiteForm, date })}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            minDate={tomorrow}
                                            locale="fr"
                                            className="form-input"
                                            placeholderText="Sélectionner une date et heure"
                                            required
                                        />
                                    </div>
                                )}
                                <div className="form-group">
                                    <label className="form-label">{t('rendezvous.qualite.comment')}</label>
                                    <textarea
                                        className="form-textarea"
                                        rows={4}
                                        value={qualiteForm.comment}
                                        onChange={(e) => setQualiteForm({ ...qualiteForm, comment: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowQualiteModal(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                    {modalLoading ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            )
            }

            {/* Dispatch Modal */}
            {
                showDispatchModal && (
                    <div className="modal-overlay" onClick={() => setShowDispatchModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3 className="modal-title">{t('rendezvous.dispatch.title')}</h3>
                            </div>
                            <form onSubmit={handleDispatchSubmit}>
                                <div className="modal-body">
                                    <div className="form-group">
                                        <label className="form-label">{t('rendezvous.dispatch.selectConseiller')} *</label>
                                        <select
                                            className="form-select"
                                            value={dispatchForm.conseiller}
                                            onChange={(e) => setDispatchForm({ ...dispatchForm, conseiller: e.target.value })}
                                            required
                                        >
                                            <option value="">-</option>
                                            {conseillers.map((c) => (
                                                <option key={c._id} value={c._id}>
                                                    {c.prenom} {c.nom}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">{t('rendezvous.dispatch.comment')}</label>
                                        <textarea
                                            className="form-textarea"
                                            rows={4}
                                            value={dispatchForm.comment}
                                            onChange={(e) => setDispatchForm({ ...dispatchForm, comment: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowDispatchModal(false)}>
                                        {t('common.cancel')}
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                        {modalLoading ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Conseiller Feedback Modal */}
            {
                showConseillerModal && (
                    <div className="modal-overlay" onClick={() => setShowConseillerModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3 className="modal-title">{t('rendezvous.conseillerFeedback.title')}</h3>
                            </div>
                            <form onSubmit={handleConseillerSubmit}>
                                <div className="modal-body">
                                    <div className="form-group">
                                        <label className="form-label">{t('rendezvous.conseillerFeedback.feedback')} *</label>
                                        <select
                                            className="form-select"
                                            value={conseillerForm.feedbackId}
                                            onChange={(e) => {
                                                const selected = qualifications.CONSEILLER.find(q => q._id === e.target.value)
                                                if (selected) {
                                                    setConseillerForm({
                                                        ...conseillerForm,
                                                        feedbackId: selected._id,
                                                        feedbackName: selected.nomQualification,
                                                        isTransfert: selected.isTransfert
                                                    })
                                                }
                                            }}
                                            required
                                        >
                                            <option value="">-</option>
                                            {qualifications.CONSEILLER.map(q => (
                                                <option key={q._id} value={q._id}>{q.nomQualification}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {conseillerForm.isTransfert && (
                                        <div className="form-group">
                                            <label className="form-label">{t('rendezvous.conseillerFeedback.newDate')}</label>
                                            <DatePicker
                                                selected={conseillerForm.date}
                                                onChange={(date) => setConseillerForm({ ...conseillerForm, date })}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                minDate={tomorrow}
                                                locale="fr"
                                                className="form-input"
                                                placeholderText="Sélectionner une date et heure"
                                                required
                                            />
                                        </div>
                                    )}
                                    <div className="form-group">
                                        <label className="form-label">{t('rendezvous.conseillerFeedback.comment')}</label>
                                        <textarea
                                            className="form-textarea"
                                            rows={4}
                                            value={conseillerForm.comment}
                                            onChange={(e) => setConseillerForm({ ...conseillerForm, comment: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowConseillerModal(false)}>
                                        {t('common.cancel')}
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={modalLoading}>
                                        {modalLoading ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div>
    )
}
